import axios, {AxiosRequestConfig} from 'axios';
import {getFileNameFromDisposition, goLogin} from '../Utils/common';
import qs from 'qs';
import Storage from 'Services/Storage';
import {UserStatus} from '../Pages/System/UserManage/service';
import Config from '../config';

export const MainRequest = axios.create({baseURL: '/api'});

MainRequest.interceptors.request.use(
    config => {
        config.headers['token'] = Storage.get<string>('token') || '';
        config.headers['x-requested-with'] = 'XMLHttpRequest';

        return config;
    },
    error => {
        return Promise.reject(error);
    },
);

MainRequest.interceptors.response.use(
    response => {
        if (response.data && typeof response.data !== 'object') goLogin();
        return response;
    },
    error => {
        if (error.response && error.response.status === 401) goLogin();
        return Promise.reject(error);
    },
);
// 负责ajax下载
MainRequest.interceptors.response.use(
    response => {
        if (response.config.responseType === 'blob') {
            // 这里只做了简单的文件名匹配, 有待完善
            const filename =
                getFileNameFromDisposition(
                    response.headers['content-disposition'],
                ) || '文件下载';

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename); //or any other extension
            document.body.appendChild(link);
            link.click();
            return response;
        }
        return response;
    },
    async error => {
        // 处理blob下载文件产生的异常
        if (error.response.config.responseType === 'blob') {
            const responseText = await error.response.data.text();
            error.response.data = JSON.parse(responseText);
        }
        return Promise.reject(error);
    },
);

export interface DictItem {
    label: string;
    value: string;
}

export interface DictItemType {
    code: string;
    name: string;
}

/**
 * 获取数据字典
 * @param code
 */
export function getDict(code: string) {
    return MainRequest.post<SuccessData<DictItemType[]>>(
        `/dict/getByParentCode`,
        {code},
    ).then(res => res.data.data);
}

/**
 * 保存用户数据
 * @param key
 * @param value
 */
export function setUserData(key: string, value: any) {
    return MainRequest.post(`/a/api/sysSet/save`, {
        functionName: key,
        content: JSON.stringify(value),
    });
}

// 模板信息
export interface TplValue {
    path: string;
    fileName: string;
}

/**
 * 读取用户数据
 * @param key
 */
export async function getUserData<T>(key: string): Promise<T | undefined> {
    const res = await MainRequest.get<SuccessData<{content: string}>>(
        `/a/api/sysSet/get?${qs.stringify({
            functionName: key,
        })}`,
    );

    try {
        return JSON.parse(res.data.data.content) as T;
    } catch (e) {
        return undefined;
    }
}

export interface ModelItem {
    id: string;
    code: string;
    name: string;
    nameEn: string;
    parentId: string;
}

/**
 * 获取特殊字典
 */
export function getListByModelName(
    modelName: string,
    data?: Record<string, any>,
) {
    return MainRequest.post<SuccessData<ModelItem[]>>(
        '/dict/getListByModelName',
        {...data, modelName},
    ).then(res => res.data.data);
}

export interface UserListItem {
    code: string;
    id: string;
    loginName: string;
    password: string;
    roleId: string[];
    defaultRoleId: string;
    status: UserStatus;
    createTime: string;
    createUser: string;
    updateTime: string;
    gender: string;
    userName: string;
}

interface UploadParams {
    onUploadProgress: (percent: number) => void;
}

/**
 * 获取七牛云上传token
 */
export function getQiniuUploadTokenSource(cancelToken?: any) {
    return MainRequest.post<SuccessData<string>>(
        '/getUploadToken',
        {},
        {cancelToken},
    ).then(res => res.data.data);
}

/**
 * 上传到七牛
 */
export async function uploadToQiNiu(file: File, options?: UploadParams) {
    const request = axios.create();
    const token = await getQiniuUploadTokenSource();
    const formData = new FormData();

    formData.append('file', file);
    formData.append('token', token);

    return request
        .post<{hash: string; key: string}>(Config.QiNiuUploadUrl, formData, {
            headers: {'Content-Type': 'multipart/form-data'},
        })
        .then(res => res.data)
        .then(data => ({
            key: data.key,
            name: file.name,
            url: `${Config.QiNiuDownloadUrl}/${
                data.key
            }?attname=${encodeURIComponent(file.name)}`,
        }));
}

/**
 * 导入文件
 * @param url
 * @param file
 * @param config 注意: 如果要传额外数据, 把数据放在config的data参数里{data: {name:"xxx"}}
 */
export function importFile(
    url: string,
    file: File,
    config?: AxiosRequestConfig,
) {
    const formData = new FormData();
    formData.set('file', file);

    if (config?.data) {
        for (const key in config.data) {
            formData.set(key, config.data[key]);
        }
    }

    return MainRequest.post<SuccessData<{error: string[]}>>(
        url,
        formData,
        config,
    )
        .then(res => res?.data?.data)
        .then(data => {
            if (data?.error?.length > 0) throw new Error(data.error.join('\n'));
        });
}

export enum Status {
    Freeze = '0', // 冻结
    Normal = '1', // 正常
}

export enum DictOpenType {
    Open = 'SWITCH10000101',
    Close = 'SWITCH10000102',
}

//获取截止时间
export function getExpirationEndTime(
    expirationStartTime: string,
    expireType: string,
) {
    return MainRequest.post<SuccessData<string>>(
        '/customerServiceVersion/getExpirationEndTime',
        {expirationStartTime, expireType},
    ).then(res => res.data.data);
}
